import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Upload, 
  Film, 
  X, 
  Check, 
  Loader2,
  Monitor,
  Smartphone,
  Lightbulb,
  AlertCircle
} from 'lucide-react';
import { api } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || '';

const AdminAddScenes = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledCollection = searchParams.get('collection') || '';
  
  const fileInputRef = useRef(null);
  
  // Estados do Formulário
  const [collectionName, setCollectionName] = useState(prefilledCollection);
  const [existingCollections, setExistingCollections] = useState([]);
  const [is4K, setIs4K] = useState(true);
  const [orientation, setOrientation] = useState('vertical');
  const [storageAccount, setStorageAccount] = useState('R2'); // R2 é o padrão agora
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  
  // Estados de Arquivos
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedScenes, setUploadedScenes] = useState([]);
  
  // Estados de Interface
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const data = await api.getCollections();
      setExistingCollections(data.map(c => c.name));
    } catch (err) {
      console.error('Erro ao carregar coleções:', err);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    
    const newFiles = videoFiles.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      status: 'pending' // Status inicial para a visualização individual
    }));
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const generateThumbnailFromVideo = (file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      let settled = false;

      const cleanup = (blob) => {
        if (settled) return;
        settled = true;
        clearTimeout(masterTimeout);
        video.pause();
        video.src = '';
        URL.revokeObjectURL(url);
        resolve(blob);
      };

      // Timeout global de 15s
      const masterTimeout = setTimeout(() => cleanup(null), 15000);

      const captureFrame = () => {
        canvas.width  = video.videoWidth  || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);

        // Verifica se o frame não é preto (pelo menos 1% de pixels não-pretos)
        const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 200), Math.min(canvas.height, 200));
        let nonBlack = 0;
        for (let i = 0; i < imageData.data.length; i += 4) {
          if (imageData.data[i] > 10 || imageData.data[i+1] > 10 || imageData.data[i+2] > 10) nonBlack++;
        }
        const ratio = nonBlack / (imageData.data.length / 4);
        if (ratio < 0.005) return false; // frame preto
        canvas.toBlob((blob) => cleanup(blob || null), 'image/jpeg', 0.88);
        return true;
      };

      const tryCapture = (attempt = 0) => {
        if (settled) return;
        if (captureFrame()) return;
        // Frame preto: tenta próximo ponto temporal
        const times = [2, 5, 1, 10, 0.5];
        const nextTime = times[attempt + 1];
        if (nextTime !== undefined && nextTime < video.duration) {
          video.currentTime = nextTime;
        } else {
          // Último recurso: faz play e pausa após 300ms para forçar decode
          video.play().then(() => {
            setTimeout(() => {
              video.pause();
              setTimeout(() => {
                captureFrame();
                if (!settled) cleanup(null);
              }, 100);
            }, 300);
          }).catch(() => cleanup(null));
        }
      };

      video.muted      = true;
      video.playsInline = true;
      video.preload    = 'auto';
      video.src        = url;

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(2, video.duration * 0.1);
      };

      video.onseeked = function onSeeked() {
        const attempt = [2, 5, 1, 10, 0.5].indexOf(video.currentTime);
        // Aguarda 80ms após o seek para garantir que o frame foi decodificado
        setTimeout(() => tryCapture(Math.max(attempt, 0)), 80);
      };

      video.onerror = () => cleanup(null);
      video.load();
    });
  };

  const handleUploadAll = async () => {
    if (!collectionName.trim() || selectedFiles.length === 0) {
      setError('Preencha o nome da coleção e selecione vídeos.');
      return;
    }
    
    setSaving(true);
    // REMOVIDO: setStep(2) - Mantemos na tela para ver o progresso individual

    let currentCount = 0;
    try {
      const packs = await api.getPacks();
      currentCount = packs.filter(p => p.collection === collectionName.trim()).length;
    } catch (e) {
      console.error("Erro ao sincronizar sequência", e);
    }

    const results = [];
    
    for (let i = 0; i < selectedFiles.length; i++) {
      const fileItem = selectedFiles[i];
      
      // Atualiza para 'uploading' na visualização individual
      setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'uploading' } : f));

      try {
        const formData = new FormData();
        formData.append('file', fileItem.file);
        
        const token = localStorage.getItem('admin_token');
        
        if (!token) {
          throw new Error('Token de autenticação não encontrado. Faça login novamente.');
        }

        const isR2 = storageAccount === 'R2' || storageAccount === 'C';
        const uploadEndpoint = isR2
          ? `${API_URL}/api/upload-video-r2`
          : `${API_URL}/api/upload-video?conta=${storageAccount}`;

        if (isR2) {
          const thumbBlob = await generateThumbnailFromVideo(fileItem.file);
          if (thumbBlob) {
            formData.append('thumb', thumbBlob, `thumb_${fileItem.file.name.replace(/\.[^.]+$/, '')}.jpg`);
          }
        }
        
        const uploadController = new AbortController();
        const uploadTimeout = setTimeout(() => uploadController.abort(), 120000); // 2 min timeout

        const uploadResponse = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
          signal: uploadController.signal
        }).finally(() => clearTimeout(uploadTimeout));
        
        if (uploadResponse.status === 401) {
          throw new Error('Sessão expirada. Por favor, faça login novamente.');
        }
        
        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || `Erro no upload: ${uploadResponse.status}`);
        }
        
        const uploadData = await uploadResponse.json();
        
        const sequence = (currentCount + i + 1).toString().padStart(2, '0');
        const autoTitle = `${collectionName.trim()} ${sequence}`;
        
        const sceneData = {
          title: autoTitle,
          collection: collectionName.trim(),
          tags: tags,
          is_4k: is4K,
          orientation: orientation,
          storage_account: storageAccount === 'R2' ? 'R2' : storageAccount,
          video_url: uploadData.video_url,
          video_public_id: uploadData.video_public_id || '',
          thumbnail_url: uploadData.thumbnail_url,
          duration: uploadData.duration,
          video_width: uploadData.width,
          video_height: uploadData.height,
          ...(isR2 && {
            storage: {
              provider: 'r2',
              bucket: 'plataformaeditorpremium',
              key: uploadData.video_key,
              keys: {
                original: uploadData.video_key,
                thumb500: uploadData.thumbnail_key || ''
              }
            }
          })
        };
        
        const scene = await api.createPack(sceneData);
        results.push(scene);
        
        // SUCESSO: A linha fica verde individualmente
        setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'success' } : f));
        
      } catch (err) {
        console.error(`Erro ao processar ${fileItem.file.name}:`, err);
        
        // Timeout do upload
        if (err.name === 'AbortError') {
          setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'error', errorMsg: 'Timeout: upload demorou mais de 2 minutos' } : f));
          continue;
        }

        // Se for erro de autenticação, para tudo e redireciona
        if (err.message.includes('Sessão expirada') || err.message.includes('Token de autenticação')) {
          setError(err.message);
          setSaving(false);
          setSelectedFiles(prev => prev.map(f => 
            f.status === 'uploading' ? { ...f, status: 'error' } : f
          ));
          
          // Redireciona para login após 3 segundos
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            navigate('/admin/login');
          }, 3000);
          return;
        }
        
        // ERRO: A linha fica vermelha individualmente
        setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'error', errorMsg: err.message } : f));
      }
    }
    
    setUploadedScenes(results);
    setSaving(false);
    
    // Pequeno delay para você ver todos os "verdes" antes de mudar de tela
    setTimeout(() => setStep(3), 1500);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-['Helvetica',_sans-serif]">
      <header className="sticky top-0 z-50 bg-[#0D0D0D] border-b border-white/5 px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/admin" className="p-2 hover:bg-white/5 rounded-lg transition-all">
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </Link>
          <h1 className="text-xl font-bold uppercase tracking-tight">Adicionar Conteúdo</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-8">
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-500 font-bold text-sm">{error}</p>
              {error.includes('Sessão expirada') && (
                <p className="text-red-400 text-xs mt-1">Redirecionando para login...</p>
              )}
            </div>
          </div>
        )}
        
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-[#0D0D0D] p-6 rounded-2xl border border-white/5">
              <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Coleção / Filme</label>
              <input
                type="text"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder="Ex: Breaking Bad"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:border-[#0056D2] outline-none transition-all"
                list="collections"
              />
              <datalist id="collections">
                {existingCollections.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#0D0D0D] p-6 rounded-2xl border border-white/5">
                <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Destino do Arquivo</label>

              {/* R2 — opção recomendada */}
              <button
                type="button"
                onClick={() => setStorageAccount('R2')}
                className={`w-full py-3 rounded-xl border font-bold text-sm mb-3 transition-all flex items-center justify-center gap-2 ${
                  storageAccount === 'R2'
                    ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                    : 'bg-white/5 border-white/10 text-gray-500 hover:text-white'
                }`}
              >
                ☁ CLOUDFLARE R2
                <span className="text-[9px] font-normal opacity-70 ml-1">RECOMENDADO</span>
              </button>

              {/* Cloudinary legado */}
              <p className="text-[10px] text-gray-600 uppercase mb-2">Cloudinary (legado)</p>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {['A', 'B', 'C'].map((acc) => (
                  <button
                    key={acc}
                    type="button"
                    onClick={() => setStorageAccount(acc)}
                    className={`py-3 rounded-xl border font-bold text-xs transition-all ${
                      storageAccount === acc 
                        ? 'bg-[#0056D2] border-[#0056D2] text-white' 
                        : 'bg-white/5 border-white/10 text-gray-500 hover:text-white'
                    }`}
                  >
                    CONTA {acc}
                    <span className="block text-[8px] opacity-60">{acc === 'C' ? 'REF · R2' : 'CENA'}</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['E', 'F', 'G'].map((acc) => (
                  <button
                    key={acc}
                    type="button"
                    onClick={() => setStorageAccount(acc)}
                    className={`py-3 rounded-xl border font-bold text-xs transition-all ${
                      storageAccount === acc 
                        ? 'bg-[#0056D2] border-[#0056D2] text-white' 
                        : 'bg-white/5 border-white/10 text-gray-500 hover:text-white'
                    }`}
                  >
                    CONTA {acc}
                    <span className="block text-[8px] opacity-60">EXPANSÃO</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {['H', 'I', 'J'].map((acc) => (
                  <button
                    key={acc}
                    type="button"
                    onClick={() => setStorageAccount(acc)}
                    className={`py-3 rounded-xl border font-bold text-xs transition-all ${
                      storageAccount === acc 
                        ? 'bg-[#0056D2] border-[#0056D2] text-white' 
                        : 'bg-white/5 border-white/10 text-gray-500 hover:text-white'
                      }`}
                    >
                      CONTA {acc}
                      <span className="block text-[8px] opacity-60">EXPANSÃO</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-[#0D0D0D] p-6 rounded-2xl border border-white/5">
                <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Orientação</label>
                <div className="flex gap-2">
                  <button onClick={() => setOrientation('vertical')} className={`flex-1 py-3 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold ${orientation === 'vertical' ? 'bg-[#0056D2] border-[#0056D2]' : 'bg-white/5 border-white/10 text-gray-500'}`}><Smartphone size={14} /> 9:16</button>
                  <button onClick={() => setOrientation('horizontal')} className={`flex-1 py-3 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold ${orientation === 'horizontal' ? 'bg-[#0056D2] border-[#0056D2]' : 'bg-white/5 border-white/10 text-gray-500'}`}><Monitor size={14} /> 16:9</button>
                </div>
              </div>
            </div>

            <div className="bg-[#0D0D0D] p-6 rounded-2xl border border-white/5">
              <div 
                onClick={() => !saving && fileInputRef.current?.click()} 
                className={`border-2 border-dashed border-white/10 rounded-2xl p-10 text-center transition-all ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-[#0056D2]/50'}`}
              >
                {storageAccount === 'C' ? <Lightbulb className="mx-auto mb-4 text-[#0056D2]" /> : <Upload className="mx-auto mb-4 text-gray-500" />}
                <p className="font-bold text-sm">Clique para selecionar os vídeos</p>
                <p className="text-xs text-gray-500 mt-1">
                  Upload para: <span className="text-orange-400 font-bold">{storageAccount === 'R2' ? '☁ Cloudflare R2' : `Conta Cloudinary ${storageAccount}`}</span>
                </p>
              </div>
              <input ref={fileInputRef} type="file" accept="video/*" multiple onChange={handleFileSelect} className="hidden" />
              
              {/* LISTA INDIVIDUAL DE UPLOADS (ISOLADO E FUNCIONAL) */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {selectedFiles.map((f) => (
                    <div key={f.id} className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-500 ${
                      f.status === 'success' ? "bg-emerald-500/10 border-emerald-500/50" : 
                      f.status === 'uploading' ? "bg-[#0056D2]/5 border-[#0056D2]/30 animate-pulse" : 
                      f.status === 'error' ? "bg-red-500/10 border-red-500/50" : "bg-white/5 border-white/5"
                    }`}>
                      <div className="flex items-center gap-3">
                        {f.status === 'success' ? (
                          <Check size={16} className="text-emerald-500" />
                        ) : f.status === 'uploading' ? (
                          <Loader2 size={16} className="text-[#0056D2] animate-spin" />
                        ) : f.status === 'error' ? (
                          <X size={16} className="text-red-500" />
                        ) : (
                          <Film size={16} className="text-gray-500" />
                        )}
                        <span className={`text-xs font-bold truncate max-w-[200px] ${f.status === 'success' ? "text-emerald-500" : "text-white"}`}>
                          {f.file.name}
                        </span>
                      </div>
                      
                      {f.status === 'pending' && !saving && (
                        <button onClick={() => setSelectedFiles(prev => prev.filter(x => x.id !== f.id))}>
                          <X size={16} className="text-gray-500 hover:text-white" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleUploadAll}
              disabled={selectedFiles.length === 0 || saving}
              className="w-full py-4 bg-[#0056D2] hover:bg-[#0066FF] disabled:opacity-50 text-white font-bold rounded-2xl transition-all shadow-lg shadow-[#0056D2]/20 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {saving ? 'PROCESSANDO UPLOAD...' : `INICIAR UPLOAD (${selectedFiles.length})`}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-emerald-500 w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold">Upload Finalizado!</h2>
            <p className="text-gray-500 mt-2 mb-8">{uploadedScenes.length} itens salvos com sucesso.</p>
            <button onClick={() => { setSelectedFiles([]); setStep(1); }} className="px-8 py-3 bg-[#0056D2] rounded-xl font-bold">Adicionar Mais</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAddScenes;
